/**
 * The header on every page: the logo, the main pages, the theme, the bell of a signed-in wallet's
 * notifications and the account area, on glass in the page's theme. On screens narrower than 1024 px (phones, and tablets held upright) the
 * pages, the theme and the account area move into a menu that opens below the header. A signed-in
 * wallet has five pages, its address and the theme to show, which do not fit in less.
 */

import { LogOut, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";

import { useSession, useSignOut } from "../auth/session.js";
import { NotificationBell, NotificationLink } from "../notifications/NotificationBell.js";
import { AccountControls, shortAddress, WalletAvatar } from "./AccountControls.js";
import { Logo } from "./Logo.js";
import { accountLinks, MAIN_LINKS } from "./navigation.js";
import { SignInDialog } from "./SignInDialog.js";
import { ThemeMenu, ThemeSwitch } from "./ThemeControls.js";
import { CopyButton, focusRing, styles } from "./ui.js";

/** The header's pages: those for signed-in wallets only once a wallet is signed in. */
function useMainLinks() {
  const session = useSession();
  return MAIN_LINKS.filter((link) => !link.signedIn || session.data);
}

export function SiteHeader({ overHero = false }: { overHero?: boolean }) {
  const mainLinks = useMainLinks();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: closes the menu on every page change.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <header
      className={`sticky top-0 z-30 border-b border-zinc-900/[0.07] text-zinc-900 backdrop-blur-xl backdrop-saturate-150 dark:border-white/[0.07] dark:text-zinc-100 ${
        // More see-through over a public page's opening, whose colours show through it.
        overHero ? "bg-white/70 dark:bg-ink-950/70" : "bg-white/85 dark:bg-ink-950/85"
      }`}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-brand-500/30 to-transparent dark:via-white/[0.08]"
      />
      <div className={`relative flex h-16 items-center justify-between gap-4 ${styles.frame}`}>
        <div className="flex items-center gap-8">
          <Logo />
          <nav aria-label="Main" className="hidden lg:block">
            <ul className="flex items-center gap-1">
              {mainLinks.map(({ to, label, current }) => (
                <li key={to}>
                  <Link
                    to={to}
                    aria-current={current(pathname) ? "page" : undefined}
                    className={`rounded-full px-3.5 py-1.5 text-sm font-medium text-zinc-600 transition-colors hover:text-zinc-950 aria-[current=page]:bg-zinc-900/[0.06] aria-[current=page]:text-zinc-950 dark:text-zinc-400 dark:hover:text-white dark:aria-[current=page]:bg-white/[0.08] dark:aria-[current=page]:text-white ${focusRing}`}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        <div className="hidden items-center gap-2 lg:flex">
          <ThemeMenu />
          <NotificationBell />
          <AccountControls />
        </div>
        <div className="flex items-center gap-1 lg:hidden">
          <NotificationLink />
          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            onClick={() => setMenuOpen((open) => !open)}
            className={`-mr-2 rounded-lg p-2 text-zinc-700 transition-colors hover:bg-zinc-900/[0.05] hover:text-zinc-950 dark:text-zinc-300 dark:hover:bg-white/[0.07] dark:hover:text-white ${focusRing}`}
          >
            {menuOpen ? (
              <X aria-hidden className="size-5" />
            ) : (
              <Menu aria-hidden className="size-5" />
            )}
          </button>
        </div>
      </div>
      {menuOpen && <SiteMenu />}
    </header>
  );
}

/**
 * The menu on screens narrower than 1024 px: the main pages, the theme, then the wallet and its
 * pages, or "Sign in".
 */
function SiteMenu() {
  const session = useSession();
  const mainLinks = useMainLinks();
  const signOut = useSignOut();
  const [dialogOpen, setDialogOpen] = useState(false);
  const itemClass =
    "flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-base font-medium text-zinc-700 transition-colors hover:bg-zinc-900/[0.05] hover:text-zinc-950 dark:text-zinc-300 dark:hover:bg-white/[0.06] dark:hover:text-white";

  return (
    <div
      id="site-menu"
      className="relative border-t border-zinc-900/[0.07] bg-white px-4 pt-3 pb-5 sm:px-6 lg:hidden dark:border-white/[0.07] dark:bg-ink-950"
    >
      <nav aria-label="Main">
        <ul>
          {mainLinks.map(({ to, label }) => (
            <li key={to}>
              <Link to={to} className={itemClass}>
                {label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <div className="mt-3 border-t border-zinc-900/[0.07] px-3 pt-4 pb-1 dark:border-white/[0.07]">
        <ThemeSwitch />
      </div>
      <div className="mt-3 border-t border-zinc-900/[0.07] pt-3 dark:border-white/[0.07]">
        {session.data ? (
          <>
            <div className="flex items-center justify-between gap-2 px-3 py-2">
              <span className="flex items-center gap-2">
                <WalletAvatar address={session.data.wallet} />
                <span className="font-mono text-sm">{shortAddress(session.data.wallet)}</span>
              </span>
              <CopyButton value={session.data.wallet} label="Copy address" />
            </div>
            <nav aria-label="Account">
              <ul>
                {accountLinks(session.data.isAdmin).map(({ to, label, icon: Icon }) => (
                  <li key={to}>
                    <Link to={to} className={itemClass}>
                      <Icon aria-hidden className="size-5 text-zinc-400" />
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <button
              type="button"
              onClick={() => signOut.mutate()}
              disabled={signOut.isPending}
              className={`w-full ${itemClass}`}
            >
              <LogOut aria-hidden className="size-5 text-zinc-400" />
              Sign out
            </button>
          </>
        ) : (
          session.isSuccess && (
            <>
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className={`w-full ${styles.primaryButton}`}
              >
                Sign in
              </button>
              <SignInDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
            </>
          )
        )}
      </div>
    </div>
  );
}
